/**
 * Shared style constants for the Phlebotomist Dashboard
 * Centralized location for consistent styling across components
 */

/**
 * NAVBB Brand Colors
 * Based on brand guidelines - see /MEMORY/brand-colors.md
 */
export const BRAND_COLORS = {
  // Primary Colors
  primary: {
    deepNavy: '#0e4d8f', // Primary brand color - headers, primary buttons
    red: '#f31a25', // Urgency, life-saving, blood-related
    royalBlue: '#1565c0', // Secondary primary - links, secondary actions
  },
  // Secondary Colors
  secondary: {
    warmBeige: '#4f6d7a', // Muted teal-gray
    coolMint: '#6e7a8a', // Cool gray-blue
    steelBlue: '#0b345f', // Dark navy
    slateGray: '#2f3a45', // Dark charcoal
    softTaupe: '#dce0e4', // Light gray
    lightGray: '#a7b4c2', // Medium gray
    lightSage: '#cedbef', // Pale blue-gray
    cloud: '#e8ecef', // Off-white
  },
};

/**
 * Dashboard Chart Colors - derived from brand colors
 * Consistent color scheme for all charts and visualizations
 */
export const CHART_COLORS = {
  // Product type colors for Expected vs Actual chart
  pRBC: {
    actual: BRAND_COLORS.primary.red, // #f31a25 - Red for actual (blood product)
    expected: '#fecaca', // Light red/pink for expected
  },
  FFP: {
    actual: '#013b79', // Darker navy for actual (was #0e4d8f deep navy)
    expected: BRAND_COLORS.secondary.lightSage, // #cedbef - Pale blue for expected
  },
  Total: {
    actual: BRAND_COLORS.secondary.slateGray, // #2f3a45 - Slate gray for actual
    expected: BRAND_COLORS.secondary.cloud, // #e8ecef - Cloud for expected
  },
  // Sub-category colors (SGL/DBL breakdown) - muted versions of parent
  'Total SGL': {
    actual: '#6b7280', // Medium gray for total single actual
    expected: '#c8ccd1', // Medium-light gray for total single expected
  },
  'Total DBL': {
    actual: '#374151', // Dark gray for total double actual
    expected: '#e5e7eb', // Light gray for total double expected
  },
  'pRBC SGL': {
    actual: '#f87171', // Lighter red for pRBC single
    expected: '#fee2e2', // Very light pink for expected
  },
  'pRBC DBL': {
    actual: '#dc2626', // Darker red for pRBC double
    expected: '#fecaca', // Light pink for expected
  },
  'FFP SGL': {
    actual: '#3b82f6', // Medium blue for FFP single
    expected: '#dbeafe', // Very light blue for expected
  },
  'FFP DBL': {
    actual: '#1e3a5f', // Dark navy for FFP double
    expected: '#bfdbfe', // Light blue for expected
  },
  // Line colors for yield percentages and trends
  lines: {
    ffpPct: BRAND_COLORS.primary.deepNavy, // Deep navy - FFP Yield %
    pRbcPct: BRAND_COLORS.primary.red, // Red - pRBC Yield %
    totalPct: BRAND_COLORS.primary.royalBlue, // Royal blue - Total Yield %
    totalAct: '#000000', // Black - Total Actual (thick line)
  },
  // Success rate trend colors
  successRates: {
    overall: BRAND_COLORS.primary.deepNavy, // Deep navy - Overall Success
    prbc: BRAND_COLORS.primary.red, // Red - pRBC Rate
    sglDonors: BRAND_COLORS.secondary.lightSage, // #cedbef - Light sage - SGL Donors
    dblDonors: BRAND_COLORS.secondary.steelBlue, // #0b345f - Steel blue - DBL Donors
  },
  // Stat colors for comparison metrics
  stats: {
    drawSuccess: BRAND_COLORS.primary.royalBlue, // Royal blue
    attempted: BRAND_COLORS.secondary.warmBeige, // Muted teal-gray
    failures: BRAND_COLORS.primary.red, // Red
    unitSuccess: BRAND_COLORS.primary.deepNavy, // Deep navy
    expected: BRAND_COLORS.secondary.coolMint, // Cool gray-blue
    actual: BRAND_COLORS.primary.royalBlue, // Royal blue
  },
  // Gauge colors
  gauge: {
    primary: BRAND_COLORS.primary.deepNavy, // Deep navy for progress
    overflow: BRAND_COLORS.primary.royalBlue, // Royal blue for >100%
    header: BRAND_COLORS.primary.deepNavy, // Deep navy header background
  },
  // Delta indicator colors (improvement/decline)
  delta: {
    positive: '#52c41a', // Green - improvement (kept for universal understanding)
    negative: BRAND_COLORS.primary.red, // Red - decline
    neutral: BRAND_COLORS.secondary.coolMint, // Gray - no change
  },
  // Speedometer gauge segments
  speedometer: {
    low: BRAND_COLORS.primary.red, // #f31a25 - Red for low (0-60%)
    medium: BRAND_COLORS.primary.deepNavy, // #0e4d8f - Deep navy for medium (60-80%)
    high: BRAND_COLORS.secondary.softTaupe, // #dce0e4 - Light gray for high (80-100%)
  },
  // Discard reason colors for Donation Failure Reasons chart (brand palette)
  discardReasons: {
    Clots: BRAND_COLORS.primary.red, // #f31a25 - Red
    LowVolume: BRAND_COLORS.primary.deepNavy, // #0e4d8f - Deep navy
    Hemolyzed: BRAND_COLORS.primary.royalBlue, // #1565c0 - Royal blue
    SealIssue: BRAND_COLORS.secondary.warmBeige, // #4f6d7a - Muted teal-gray
    BrokenBag: BRAND_COLORS.secondary.steelBlue, // #0b345f - Dark navy
    RedCellContamination: BRAND_COLORS.secondary.coolMint, // #6e7a8a - Cool gray-blue
    DonorHealthIssue: BRAND_COLORS.secondary.lightSage, // #cedbef - Pale blue-gray
    Lipemic: BRAND_COLORS.secondary.slateGray, // #2f3a45 - Dark charcoal
    Other: BRAND_COLORS.secondary.lightGray, // #a7b4c2 - Medium gray
    Timeout: '#faad14', // Amber - displayed as "Unsuccessful"
    Expired: '#722ed1', // Purple
  } as Record<string, string>,
  // Display mode button colors (Split Screen, Inline Compare, Trends)
  displayModeButton: {
    active: BRAND_COLORS.primary.royalBlue, // #1565c0 - Royal blue for active buttons
    activeText: '#ffffff', // White text for active buttons
  },
  // Checkbox colors (Region, Company)
  checkbox: {
    checked: BRAND_COLORS.primary.royalBlue, // #1565c0 - Royal blue for checked state
  },
  // Filter bar colors
  filterBar: {
    current: {
      background: BRAND_COLORS.secondary.lightSage, // Light sage background
      border: BRAND_COLORS.primary.royalBlue, // Royal blue border
      text: BRAND_COLORS.primary.deepNavy, // Deep navy text
    },
    comparison: {
      background: BRAND_COLORS.secondary.softTaupe, // Soft taupe background
      border: BRAND_COLORS.secondary.coolMint, // Cool mint border
      text: BRAND_COLORS.secondary.slateGray, // Slate gray text
    },
  },
};

/**
 * Drop shadow settings for dashboard elements
 * Used on bars, charts, gauges, etc.
 */
export const DASHBOARD_SHADOWS = {
  /** Light shadow for subtle depth on smaller elements like progress bars */
  light: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)',
  /** Medium shadow for charts and gauges */
  medium: '0 2px 6px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
  /** Strong shadow for prominent elements */
  strong: '0 4px 12px rgba(0, 0, 0, 0.18), 0 2px 4px rgba(0, 0, 0, 0.12)',
};

/**
 * SVG filter definition for drop shadows
 * Use this in SVG elements (charts, gauges)
 */
export const SVG_SHADOW_FILTER = {
  id: 'dashboardShadow',
  dx: 0,
  dy: 2,
  stdDeviation: 3,
  floodOpacity: 0.2,
};

/**
 * Get CSS box-shadow for progress bars
 */
export const getProgressBarShadow = () => DASHBOARD_SHADOWS.light;

/**
 * Get CSS box-shadow for chart containers
 */
export const getChartShadow = () => DASHBOARD_SHADOWS.medium;

/**
 * Get CSS box-shadow for gauge elements
 */
export const getGaugeShadow = () => DASHBOARD_SHADOWS.medium;

/**
 * Get CSS drop-shadow filter for SVG elements that can't use box-shadow
 * (e.g., speedometers, gauges rendered with SVG)
 */
export const getDropShadowFilter = () =>
  `drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2))`;
